import { isImage, isPdf } from '@idc-forms/client-app-domain/documents/document-kind'
import { Download, FileQuestion } from 'lucide-react'
import { CapImageViewer } from '@/features/client-app-preview/components/cap-image-viewer'
import { CapPdfViewer } from '@/features/client-app-preview/components/cap-pdf-viewer'
import { CAP_ROOT_CLASS } from '@/features/client-app-preview/theme/cap-tokens'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/shared/ui/dialog'
import { Spinner } from '@/shared/ui/spinner'
import { formatBytes } from '@/shared/utils/handleData'
import { fileKindLabel } from '../utils/file-kind'

export type DocumentViewerDialogProps = {
  /** El documento abierto, o `null` con el visor cerrado. */
  document: { id: string; name: string; mimeType: string | null } | null
  onOpenChange: (open: boolean) => void
  /** La firma de descarga. Ausente mientras viaja. */
  url: string | null
  fileName?: string
  size?: number | null
  isPending: boolean
  isError: boolean
}

/**
 * EL VISOR DEL CARTAPACIO: el mismo que ve el cliente, antes de publicarlo.
 *
 * ## Por qué reusa los visores de la app y no abre una pestaña
 *
 * Porque la pregunta que el curador se hace antes de publicar es «¿cómo va a ver esto el
 * cliente?», y una pestaña del navegador con el visor nativo de Chrome responde otra. Los dos
 * visores de la previsualización —`CapPdfViewer` y `CapImageViewer`— son los MISMOS que dibuja la
 * app, así que lo que se ve acá es lo que va a ver quien lo abra en el teléfono.
 *
 * Es además la respuesta a lo que hacía falta de verdad: hasta ahora se publicaba mirando el
 * nombre del archivo.
 *
 * ## El `cap-` va en el CONTENIDO, no en el diálogo
 *
 * El `DialogContent` se porta a `body`, o sea **fuera** del lienzo del cartapacio, así que los
 * tokens `--cap-` no llegan por herencia: los visores renderizarían sin una sola variable
 * resuelta. Se enciende acá adentro, sobre el nodo que envuelve al visor — y sólo ahí: el
 * encabezado y los botones del diálogo siguen siendo chrome del back-office y tienen que
 * parecerlo.
 *
 * ## Lo que no se puede previsualizar se DICE, no se intenta
 *
 * Un `.docx` o una planilla no tienen visor ni acá ni en la app. En vez de dejar un marco en
 * blanco o un visor roto, se ofrece descargarlo — que es lo único que se puede hacer con ese
 * archivo, y es exactamente lo que la app le ofrece al cliente.
 */
export function DocumentViewerDialog({
  document,
  onOpenChange,
  url,
  fileName,
  size,
  isPending,
  isError
}: DocumentViewerDialogProps) {
  const pdf = isPdf(document?.mimeType)
  const image = isImage(document?.mimeType)

  return (
    <Dialog
      open={Boolean(document)}
      onOpenChange={(open) => {
        if (!open) onOpenChange(false)
      }}
    >
      <DialogContent className="flex max-h-[85vh] flex-col gap-3 sm:max-w-2xl">
        <DialogHeader className="pr-6">
          <DialogTitle className="truncate text-base" title={document?.name}>
            {document?.name}
          </DialogTitle>
          <DialogDescription>
            {fileKindLabel(document?.mimeType ?? null)}
            {typeof size === 'number' ? ` · ${formatBytes(size)}` : ''} · Así lo ve el cliente en su
            app.
          </DialogDescription>
        </DialogHeader>

        {/*
         * Alto fijo y no `auto`: el visor de PDF pagina contra la altura de su contenedor, y con
         * un alto que depende del contenido se mide a sí mismo en cero en el primer frame.
         */}
        <div
          className={`${CAP_ROOT_CLASS} cap-bg-neutral-50 relative min-h-0 flex-1 overflow-hidden rounded-lg`}
        >
          {isPending ? (
            <div className="flex h-full min-h-64 items-center justify-center">
              <Spinner className="h-5 w-5" />
            </div>
          ) : isError || !url ? (
            <div className="flex h-full min-h-64 flex-col items-center justify-center gap-2 px-6 text-center">
              <FileQuestion className="h-8 w-8 text-muted-foreground" aria-hidden />
              <p className="text-sm font-medium">No se pudo abrir el documento</p>
              <p className="text-xs text-muted-foreground">
                Vuelve a intentarlo; si sigue igual, descárgalo para revisarlo.
              </p>
            </div>
          ) : pdf ? (
            /*
             * Altura DEFINIDA, no `h-full`: el lienzo es un flex item con altura indefinida y
             * el visor de pdf.js pinta sólo las páginas que caen dentro de su contenedor de
             * scroll. Con `h-full min-h-[60vh]` el `min-h` daba los 60vh al marco gris, pero el
             * `h-full` de adentro resolvía a 0 px: contenedor de 0 px, ninguna página «visible»,
             * ningún canvas — el diálogo abría vacío. Medido en staging: 622×0.
             * El `min(…)` deja lugar al encabezado y al pie dentro de los 85vh del diálogo.
             */
            <div className="h-[min(60vh,calc(85vh-13rem))]">
              <CapPdfViewer url={url} />
            </div>
          ) : image ? (
            <CapImageViewer
              url={url}
              isPending={false}
              className="h-[min(50vh,calc(85vh-13rem))]"
            />
          ) : (
            <div className="flex h-full min-h-64 flex-col items-center justify-center gap-2 px-6 text-center">
              <FileQuestion className="h-8 w-8 text-muted-foreground" aria-hidden />
              <p className="text-sm font-medium">Este tipo de archivo no se previsualiza</p>
              <p className="text-xs text-muted-foreground">
                El cliente tampoco lo ve dentro de la app: lo descarga para abrirlo.
              </p>
            </div>
          )}
        </div>

        {url ? (
          <div className="flex justify-end">
            {/*
             * Un `<a download>` y no un botón con `window.open`: así funcionan el clic del medio,
             * el «guardar como» del menú contextual y Cmd+clic, que es lo que alguien va a hacer
             * con un documento que quiere archivar.
             */}
            <Button asChild size="sm" variant="outline">
              <a href={url} download={fileName} target="_blank" rel="noreferrer">
                <Download className="h-4 w-4" />
                Descargar
              </a>
            </Button>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
